<script setup lang="ts">
import { computed } from 'vue'
import type { IHeaderContent } from '@/legacy/ts/header'
import HeaderAuth from '@/components/HeaderAuth.vue'
import {
	getVueAssetImagePath,
	breakPointVariables,
	getCurrentGaPageName,
} from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'

defineProps({
	isLayoutA: {
		type: Boolean,
		default: true,
	},
})
const typeSuccessDialog = 'bns'
const { HeaderContent }: { HeaderContent: IHeaderContent } = window
const { mobile, tabletLandscape, desktop } = breakPointVariables
const { heroContent } = window.pageContent
const introTitleBlack = computed(() => heroContent?.layoutA?.introTitleBlack)
const introTitleOrange = computed(() => heroContent?.layoutA?.introTitleOrange)
const introSubtitle = computed(() => heroContent?.layoutA?.introSubtitle)
const listMilestones = computed(() => heroContent?.layoutA?.listMilestones)
const listIcons = ['people', 'manage_order', 'customer_service']

const parsedSegments = computed(() => {
	const text = heroContent?.layoutB?.signupText || ''
	const segments = []
	const regex = /\{(.*?)\}|([^{}]+)/g

	let match = regex.exec(text)
	while (match !== null) {
		if (match[1]) {
			segments.push({ content: match[1], isHighlight: true })
		} else if (match[2]) {
			segments.push({ content: match[2], isHighlight: false })
		}
		match = regex.exec(text)
	}

	return segments
})
const formatWelcomeText = computed(() => {
	const settings = window.BUYANDSHIP?.settings || {}
	const welcomeBonus = settings.member_welcome_credits || ''
	const currency = settings.current_country2.currency_symbol || ''
	const welcomeText = heroContent?.layoutB?.welcomeText || ''

	return welcomeText
		?.replace(/{WELCOME_CREDIT}/g, String(welcomeBonus))
		.replace(/{CURRENCY}/g, currency)
})

function onClickStartWithEamil() {
	window.BsHeaderVm.showAuthPanel()
	createGAEvent(GaGeneralEvent.ButtonClick, {
		...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
		button_name: 'Start With Email - Hero',
	})
}

function onClickSignup() {
	window.BsHeaderVm.showAuthPanel()
	createGAEvent(GaGeneralEvent.ButtonClick, {
		...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
		button_name: 'Signup With Other - Hero',
	})
}

function onClickGoogle() {
	if (window.BsHeaderVm.$refs?.headerAuth) {
		window.BsHeaderVm.$refs?.headerAuth.googleSignIn()
	}
	createGAEvent(GaGeneralEvent.ButtonClick, {
		...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
		button_name: 'Google SSO Click - Hero',
	})
}
</script>

<template>
	<section
		class="hero-section-a relative max-w-full overflow-hidden bg-o-50"
		v-if="isLayoutA"
	>
		<picture
			class="absolute bottom-0 left-1/2 w-[767px] -translate-x-1/2 mdp:left-[unset] mdp:right-[-37%] mdp:w-[1280px] mdp:translate-x-[unset] mdl:right-[-15%] lg:left-[48%] lg:right-[unset] lg:w-[1200px]"
		>
			<source
				:media="`(max-width: ${Number(mobile) - 1}px)`"
				:srcset="getVueAssetImagePath('hero-section-ph_globe_sm_a.png')"
			/>
			<source
				:media="`(max-width: ${Number(desktop) - 1}px)`"
				:srcset="getVueAssetImagePath('hero-section-ph_globe_md_a.png')"
			/>
			<source
				:srcset="getVueAssetImagePath('hero-section-ph_globe_lg_a.png')"
			/>
			<img
				class="w-full"
				:srcset="`
						${getVueAssetImagePath('hero-section-ph_globe_lg_a.png')}
					`"
				alt="hero section a"
			/>
		</picture>
		<div
			class="bs-container relative flex flex-wrap items-center gap-24px py-acquaintances mdp:gap-20px"
		>
			<div class="basis-full mdp:flex-1">
				<h1 class="typography-h1-str mb-16px">
					{{ introTitleBlack }}
					<span class="text-o-500">{{ introTitleOrange }}</span>
				</h1>
				<p class="typography-b1-reg">{{ introSubtitle }}</p>
				<div class="mt-casual-friends flex flex-col gap-20px lg:flex-row">
					<div
						class="inline-flex items-center lg:flex-1"
						v-for="(icon, index) in listIcons"
						:key="index"
					>
						<div class="mr-16px h-24px w-24px lg:h-32px lg:w-32px">
							<BaseIcon :name="icon" class="h-full w-full fill-n-950" />
						</div>
						<div
							class="typography-s1-reg flex-1"
							v-html="listMilestones[index]"
						></div>
					</div>
				</div>
			</div>
			<div
				class="relative flex basis-full justify-center mdp:flex-1 mdp:justify-end"
			>
				<HeaderAuth
					:is-show-auth-dialog="false"
					:type-success-dialog="typeSuccessDialog"
					:text="HeaderContent.authContent"
					:is-expose-entrance-only="true"
				/>
			</div>
		</div>
	</section>

	<section
		class="hero-section-b relative max-w-full overflow-hidden bg-gradient-to-b from-o-50 to-white"
		v-else
	>
		<picture
			:class="`absolute bottom-0 left-1/2 w-[1000px] -translate-x-1/2 mdp:w-[168%] mdl:w-[120%] lg:w-[1245px] [@media(min-width:820px)_and_(max-width:${
				Number(tabletLandscape) - 1
			}px)]:w-[155%]`"
		>
			<source
				:media="`(max-width: ${Number(mobile) - 1}px)`"
				:srcset="getVueAssetImagePath('hero-section-ph_globe_sm_b.png')"
			/>
			<source
				:media="`(max-width: ${Number(desktop) - 1}px)`"
				:srcset="getVueAssetImagePath('hero-section-ph_globe_md_b.png')"
			/>
			<source
				:srcset="getVueAssetImagePath('hero-section-ph_globe_lg_b.png')"
			/>
			<img
				class="w-full"
				:srcset="`
						${getVueAssetImagePath('hero-section-ph_globe_lg_a.png')}
					`"
				alt="hero section a"
			/>
		</picture>
		<div
			class="relative px-20px pb-strangers pt-distant-acquaintances mdp:px-32px"
		>
			<div class="text-center mdp:mx-auto mdp:max-w-[95%] mdl:max-w-[796px]">
				<h1 class="typography-h1-str mb-16px">
					{{ introTitleBlack }}
					<span class="text-o-500">{{ introTitleOrange }}</span>
				</h1>
				<p class="typography-b1-reg text-n-500">
					{{ introSubtitle }}
				</p>
				<p class="typography-s2-reg mb-32px mt-16px text-n-500">
					{{ formatWelcomeText }}
				</p>
				<div class="mdp:mx-auto mdp:w-[90%] lg:w-3/4">
					<div
						class="inline-flex flex-col gap-12px mdp:w-full mdp:flex-row mdp:px-16px"
					>
						<div class="mdp:flex-1 lg:text-right">
							<BaseButton
								class="lg:max-w-[300px]"
								size="large"
								variant="primary"
								:isFull="true"
								@click="onClickStartWithEamil"
							>
								{{ heroContent?.layoutB?.buttonEmail }}
							</BaseButton>
						</div>
						<div class="mdp:flex-1 lg:text-left">
							<BaseButton
								class="lg:max-w-[300px]"
								size="large"
								variant="secondary"
								:isFull="true"
								@click="onClickGoogle"
							>
								{{ heroContent?.layoutB?.buttonGoogle }}
								<template #leadingIcon>
									<BaseIcon name="login_google" />
								</template>
							</BaseButton>
						</div>
					</div>
					<div class="my-8px flex h-20px text-center">
						<span class="relative top-8px h-[1px] flex-auto bg-n-100"></span>
						<span class="typography-c1-reg basis-[40px] text-n-200">
							{{ HeaderContent.authContent.divider_or }}
						</span>
						<span class="relative top-8px h-[1px] flex-auto bg-n-100"></span>
					</div>
				</div>
				<div class="block pb-72px mdp:pb-48px lg:pb-32px">
					<span v-for="(segment, index) in parsedSegments" :key="index">
						<template v-if="segment.isHighlight">
							<span
								class="cursor-pointer text-o-500 underline"
								@click="onClickSignup"
							>
								{{ segment.content }}
							</span>
						</template>
						<template v-else>
							{{ segment.content }}
						</template>
					</span>
				</div>
			</div>
		</div>
	</section>
</template>
